import { useCallback, useEffect, useRef, useState } from "react";
import { IProjectsService } from "../services/NewformaApi/IProjectsService";
import { Paging } from "../models/ProjectKeywordsResponse";
import { Project } from "../models/ProjectsResponse";
import { IProjectData } from "../models/shared/IProjectData";

export interface UseFilteredProjectsOptions {
    projectsService: IProjectsService;
    debounceMs?: number;
    maxServerResults?: number;
    /** When true, automatically primes the backend on first mount. */
    autoPrime?: boolean;
}

export interface UseFilteredProjectsResult {
    searchResults: IProjectData[];
    isSearching: boolean;
    setQuery: (query: string) => void;
    prime: (mask: string) => void;
}

const DEFAULT_DEBOUNCE_MS = 500;
const DEFAULT_MAX_SERVER_RESULTS = 100;

// Static priming state shared across all hook instances (once per session)
let primePromise: Promise<IProjectData[]> | null = null;
let primedProjects: IProjectData[] | null = null;

/** Exported for testing — resets the static priming state. */
export function resetPrimingState(): void {
    primePromise = null;
    primedProjects = null;
}

function mapProjectToData(project: Project): IProjectData {
    return {
        name: project.name,
        number: project.number,
        displayName: project.number ? `${project.number} - ${project.name}` : project.name,
        nrn: project.nrn,
        nixEnabled: project.nixEnabled,
        code: project.code,
        autoFileMailboxFolderName: project.autoFileMailboxFolderName,
    };
}

export function useFilteredProjects(options: UseFilteredProjectsOptions): UseFilteredProjectsResult {
    const {
        projectsService,
        debounceMs = DEFAULT_DEBOUNCE_MS,
        maxServerResults = DEFAULT_MAX_SERVER_RESULTS,
    } = options;

    const [searchResults, setSearchResults] = useState<IProjectData[]>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Track last completed fetch for local refinement optimization
    const lastFetchRef = useRef<{ textLower: string; results: IProjectData[]; isComplete: boolean }>({
        textLower: "",
        results: [],
        isComplete: false,
    });

    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const fetchProjects = useCallback(
        async (trimmed: string) => {
            // Cancel any in-flight request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            const controller = new AbortController();
            abortControllerRef.current = controller;

            setIsSearching(true);

            try {
                const paging: Paging = { offsetToken: "globalProject" };
                const maskWithLimit = `${trimmed}:${maxServerResults}`;
                const response = await projectsService.getGlobalProjects(paging, maskWithLimit);

                if (controller.signal.aborted || !mountedRef.current) return;

                const projects = (response.projects ?? []).map(mapProjectToData);
                const isComplete = projects.length < maxServerResults;

                lastFetchRef.current = {
                    textLower: trimmed.toLowerCase(),
                    results: projects,
                    isComplete,
                };

                setSearchResults(projects);
            } catch (error) {
                if (controller.signal.aborted || !mountedRef.current) return;
                setSearchResults([]);
                lastFetchRef.current = { textLower: trimmed.toLowerCase(), results: [], isComplete: false };
            } finally {
                if (!controller.signal.aborted && mountedRef.current) {
                    setIsSearching(false);
                }
            }
        },
        [projectsService, maxServerResults]
    );

    const setQuery = useCallback(
        (query: string) => {
            const trimmed = query.trim();
            const trimmedLower = trimmed.toLowerCase();

            // Clear debounce timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
                debounceTimerRef.current = null;
            }

            // Less than 3 chars: clear results, no server call
            if (trimmedLower.length < 3) {
                setSearchResults([]);
                setIsSearching(false);
                if (abortControllerRef.current) {
                    abortControllerRef.current.abort();
                    abortControllerRef.current = null;
                }
                return;
            }

            // Local refinement: if last fetch was a prefix and was complete, filter locally
            const last = lastFetchRef.current;
            if (last.isComplete && last.textLower && trimmedLower.startsWith(last.textLower)) {
                const filtered = last.results.filter((p) => p.displayName.toLowerCase().includes(trimmedLower));
                setSearchResults(filtered);
                setIsSearching(false);
                return;
            }

            // Debounce the API call
            setIsSearching(true);
            debounceTimerRef.current = setTimeout(() => {
                fetchProjects(trimmed);
            }, debounceMs);
        },
        [fetchProjects, debounceMs]
    );

    const prime = useCallback(
        (mask: string) => {
            if (primePromise) return;

            primePromise = (async () => {
                try {
                    const paging: Paging = { offsetToken: "globalProject" };
                    const response = await projectsService.getGlobalProjects(paging, mask);
                    const projects = (response.projects ?? []).map(mapProjectToData);
                    primedProjects = projects;
                    return projects;
                } catch {
                    primedProjects = [];
                    return [];
                }
            })();
        },
        [projectsService]
    );

    // Auto-prime on mount when enabled
    useEffect(() => {
        if (options.autoPrime) {
            prime("ed:100");
        }
    }, [options.autoPrime, prime]);

    return { searchResults, isSearching, setQuery, prime };
}
